import { useCallback } from 'react';

/**
 * Custom hook to manage context menu for trees
 * 
 * @param {Array} items - Array of tree items
 * @param {Function} findItemById - Function to find an item by its ID
 * @param {Array} selected - Array of selected item IDs
 * @param {Function} setContextMenu - Function to set context menu state
 * @returns {Object} - Object containing context menu handlers
 */
export default function useTreeContextMenu(items, findItemById, selected, setContextMenu) {
  // Handler for context menu events
  const handleContextMenu = useCallback((event, itemId, item) => {
    // Prevent default browser context menu
    event.preventDefault();
    event.stopPropagation();
    
    console.log("Opening context menu for:", { itemId, item });
    
    // Set context menu state with position and item information
    setContextMenu({
      mouseX: event.clientX,
      mouseY: event.clientY,
      id: itemId,
      item: item,
      selectedItems: selected.includes(itemId) ? selected : [itemId]
    });
  }, [selected, setContextMenu]);
  
  // Helper to extract item ID from DOM element
  const getTreeItemId = useCallback((element) => {
    if (!element) return null;
    
    // Try to get data directly from element's __item property (MUI X)
    if (element.__item && element.__item.id) {
      return element.__item.id;
    }
    
    // MUI TreeView pattern: "mui-tree-view-{number}-{itemId}"
    if (element.id && /^mui-tree-view-\d+-/.test(element.id)) {
      const id = element.id.replace(/^mui-tree-view-\d+-/, '');
      return id;
    }
    
    // Check data attribute - expanded to check more attributes
    const dataId = element.getAttribute('data-item-id') || 
                  element.getAttribute('data-id') || 
                  element.dataset?.itemId;
    if (dataId) return dataId;
    
    // Check aria attributes
    const ariaRowindex = element.getAttribute('aria-rowindex');
    if (ariaRowindex && items[parseInt(ariaRowindex, 10) - 1]) {
      return items[parseInt(ariaRowindex, 10) - 1].id;
    }
    
    // Check aria-labelledby which often contains the ID
    if (element.getAttribute('aria-labelledby')) {
      const labelId = element.getAttribute('aria-labelledby');
      const labelElem = document.getElementById(labelId);
      if (labelElem && labelElem.dataset && labelElem.dataset.itemId) {
        return labelElem.dataset.itemId;
      }
    }
    
    // Check parent if this element doesn't have ID
    return element.parentElement ? getTreeItemId(element.parentElement) : null;
  }, [items]);
  
  // Generate context menu handler for tree item
  const getContextMenuHandler = useCallback(() => (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    // Check if the item data is available directly on the element
    let itemId;
    let item = null;
    
    if (event.currentTarget && event.currentTarget.__item) {
      // Direct access to item data (available in MUI X TreeView)
      item = event.currentTarget.__item;
      itemId = item.id;
    } else {
      // Try to extract item ID from DOM
      itemId = getTreeItemId(event.currentTarget);
      
      // If we have the ID, find the item
      if (itemId) {
        item = findItemById(items, itemId);
      }
    }
    
    // If we found both an ID and an item, show the context menu
    if (itemId && item) {
      handleContextMenu(event, itemId, item);
    } else {
      console.error("Failed to identify item for context menu:", { 
        element: event.currentTarget,
        itemId,
        item
      });
    }
  }, [items, getTreeItemId, handleContextMenu, findItemById]);
  
  return {
    handleContextMenu,
    getContextMenuHandler,
    getTreeItemId
  };
}
